import React from 'react';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';

import {
  useCheckoutCartState,
  useFeatureIsOn,
} from '@monofront/business/contexts';
import {
  ABTEST_CHECKOUT_PAYMENT_CTA_FROM_STICKY_INTO_SUMMARY,
  ABTEST_CHECKOUT_PAYMENT_MAAP_BLOCK,
  ABTEST_CHECKOUT_PAYMENT_TEXT_POSITION,
  ABTEST_CHECKOUT_MONTHLY_MEMBERSHIP,
  CHECKOUT_STATE_INDEXES,
  FEATURE_CHECKOUT_ROUND_UP,
} from '@monofront/business/constants';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import Divider from '@monofront/design-system/components/Divider';
import {
  CheckoutStateEnum,
  ICheckoutPageContent,
  MembershipPlan,
} from '@monofront/types';
import { trackEvent } from '@monofront/tracking';

import CustomError from '@screens/CustomError';
import Coupon from '@features/Coupon';
import CheckoutSummaryMobile from '@features/CheckoutSummary/CheckoutSummaryMobile';
import CheckoutComModal3DS from '@features/PaymentMethods/CheckoutComModal3DS';
import { Body, NextAction } from '@components/LayoutCheckout';
import NeedHelp from '@components/NeedHelp';

import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';

import PaymentMethodsList from './PaymentMethodsList';
import PaymentMethodsListExisting from './PaymentMethodsListExisting';
import PaymentMethodsListNew from './PaymentMethodsListNew';
import PaymentActions from './PaymentActions';
import MessageFreeOrder from './MessageFreeOrder';
import MessageMaap from './MessageMaap';
import MessageWarning from './MessageWarning';
import { useCheckoutPayment } from './contexts';
import GeneralConditions from '@components/GeneralConditions';
import { useEffectOnce, useMediaQuery } from '@monofront/business/hooks';
import MembershipPlanPicker from './MembershipPlanPicker';
import { useProductInfosInCheckout } from '@hooks/businessContainer';
import { MembershipProductSummary } from '@features/CheckoutSummary';
import { useAuthentication } from '@context/businessContainer';
import { useCheckoutFlowEnabledForCheckout } from '@hooks/useCheckoutFlowEnabled';

interface ICheckoutPaymentProps {
  pageContents?: Partial<ICheckoutPageContent>;
}

const CheckoutPayment: React.FC<ICheckoutPaymentProps> = ({ pageContents }) => {
  const router = useRouter();
  const tErrors = useTranslations('Errors');
  const tCart = useTranslations('Cart');
  const { totalEconomy } = useProductInfosInCheckout();
  const { isSubscriptionAvailable, isLoading } =
    useSubscribeInCheckout(useAuthentication);
  const { error, checkoutCart, membershipPlan } = useCheckoutCartState();
  const { isPaymentPageReady } = useCheckoutPayment();
  const trackingEventSent = useRef(false);
  const isRoundUpFeatureEnabled = useFeatureIsOn(FEATURE_CHECKOUT_ROUND_UP);
  const isPaymentTextPositionABTestEnabled = useFeatureIsOn(
    ABTEST_CHECKOUT_PAYMENT_TEXT_POSITION,
  );
  const isPaymentMaapBlockABTestEnabled = useFeatureIsOn(
    ABTEST_CHECKOUT_PAYMENT_MAAP_BLOCK,
  );
  const isPaymentCTAFromStickyIntoSummaryAbTestEnabled = useFeatureIsOn(
    ABTEST_CHECKOUT_PAYMENT_CTA_FROM_STICKY_INTO_SUMMARY,
  );
  const showNewSummaryMaapBlock =
    isPaymentMaapBlockABTestEnabled ||
    isPaymentCTAFromStickyIntoSummaryAbTestEnabled;

  const isCheckoutFlowEnabled = useCheckoutFlowEnabledForCheckout();
  const isMobile = useMediaQuery(800);
  const isCheckoutMonthlyMembershipABTestEnabled = useFeatureIsOn(
    ABTEST_CHECKOUT_MONTHLY_MEMBERSHIP,
  );
  const showOldMaapBlockOnPaymentPageMobile =
    isMobile &&
    !isPaymentMaapBlockABTestEnabled &&
    isCheckoutMonthlyMembershipABTestEnabled;

  useEffectOnce(
    () => checkoutCart != null,
    () => {
      // Redirect to Shipping page if CheckoutCart has no shipping
      if (
        checkoutCart &&
        CHECKOUT_STATE_INDEXES[checkoutCart?.checkoutState] <
          CHECKOUT_STATE_INDEXES[CheckoutStateEnum.SHIPPING_SELECTED]
      ) {
        toast.warning(tErrors('deliveryMethodNotSelected'));
        router.push('/checkout/delivery');
      }
    },
    [checkoutCart],
  );

  useEffect(() => {
    // Track page
    if (
      !trackingEventSent.current &&
      checkoutCart?.shipments?.[0] &&
      !isLoading
    ) {
      const products = checkoutCart.items.map(
        ({ name, sku, quantity, unitPrice, productVendor }) => ({
          name,
          id: sku,
          quantity,
          price: unitPrice / 100,
          brand: productVendor,
        }),
      );

      trackEvent({
        type: 'LFAddShippingInfo',
        data: {
          currency: checkoutCart.currencyCode,
          value: checkoutCart.total / 100,
          isMaap: isSubscriptionAvailable,
          shipping: checkoutCart.shipments[0],
          promotionCoupon: checkoutCart.promotionCoupon,
          products: products.map((product) => ({
            ...product,
            sku: product.id,
            price: product.price * 100,
          })),
        },
      });

      trackingEventSent.current = true;
    }
  }, [checkoutCart, isSubscriptionAvailable]);

  if (error)
    return (
      <Body>
        <CustomError statusCode={404} />
      </Body>
    );

  if (!checkoutCart || !isPaymentPageReady) return <HeartLoader />;

  return (
    <>
      <MessageWarning
        UNSAFE_className="mobile-only"
        pageContents={pageContents}
      />
      {!showNewSummaryMaapBlock && isMobile && (
        <CheckoutSummaryMobile withRoundUp={isRoundUpFeatureEnabled} />
      )}
      <Body>
        {isSubscriptionAvailable &&
          isCheckoutMonthlyMembershipABTestEnabled && (
            <MembershipPlanPicker savings={totalEconomy} />
          )}
        <MessageWarning
          UNSAFE_className="desktop-only"
          pageContents={pageContents}
        />
        {isSubscriptionAvailable && !isPaymentTextPositionABTestEnabled && (
          <MessageMaap
            className="tw-lf-body-regular-md tw-mb-8 tw-mt-6 desktop:tw-mb-8 desktop:tw-mt-2"
            paymentText={
              !isCheckoutMonthlyMembershipABTestEnabled
                ? pageContents?.paymentText
                : membershipPlan === MembershipPlan.YEARLY
                ? pageContents?.paymentTextYearly
                : pageContents?.paymentTextMonthly
            }
          />
        )}
        {CheckoutStateEnum.PAYMENT_SKIPPED === checkoutCart.checkoutState ? (
          <>
            <MessageFreeOrder />
            {/* Under Flow the CTA lives inside a payment row; a free order has none. */}
            {isCheckoutFlowEnabled && <PaymentActions />}
          </>
        ) : showNewSummaryMaapBlock ? (
          <div className="[&>section]:tw-mb-8">
            {!isCheckoutMonthlyMembershipABTestEnabled && (
              <h3 className="tw-lf-body-bold-md tw-mb-4 desktop:tw-hidden">
                {tCart('choosePaymentMethod')}
              </h3>
            )}

            <CheckoutComModal3DS />
            {isCheckoutFlowEnabled ? (
              <PaymentMethodsList checkoutCart={checkoutCart} />
            ) : (
              <>
                <PaymentMethodsListExisting checkoutCart={checkoutCart} />
                <PaymentMethodsListNew checkoutCart={checkoutCart} />
              </>
            )}
          </div>
        ) : (
          <>
            {isMobile && (
              <NextAction
                isMembershipSummaryDisplayed={isSubscriptionAvailable}
              >
                {!isCheckoutFlowEnabled && <PaymentActions />}
                <GeneralConditions
                  isCheckout
                  customStyle="tw-mt-4 desktop:tw-block tw-hidden tw-text-center"
                />
              </NextAction>
            )}

            <CheckoutComModal3DS />
            {isCheckoutFlowEnabled ? (
              <PaymentMethodsList checkoutCart={checkoutCart} />
            ) : (
              <>
                <PaymentMethodsListExisting checkoutCart={checkoutCart} />
                <PaymentMethodsListNew checkoutCart={checkoutCart} />
              </>
            )}
            {showOldMaapBlockOnPaymentPageMobile && (
              <div className="tw-mt-4">
                <MembershipProductSummary />
              </div>
            )}
          </>
        )}
        {isMobile && (
          <div>
            {showNewSummaryMaapBlock && (
              <NextAction
                isMembershipSummaryDisplayed={isSubscriptionAvailable}
              >
                {!isCheckoutFlowEnabled && <PaymentActions />}
                {isPaymentCTAFromStickyIntoSummaryAbTestEnabled && (
                  <GeneralConditions
                    isCheckout
                    customStyle="tw-mt-4 tw-text-center"
                  />
                )}
              </NextAction>
            )}
            {isSubscriptionAvailable && isPaymentTextPositionABTestEnabled && (
              <MessageMaap
                paymentText={pageContents?.paymentText}
                className="tw-lf-body-regular-sm tw-my-8"
              />
            )}
            <Divider />
            <Coupon />
            <Divider />
            <NeedHelp />
          </div>
        )}
        {isSubscriptionAvailable && isPaymentTextPositionABTestEnabled && (
          <MessageMaap
            paymentText={
              !isCheckoutMonthlyMembershipABTestEnabled
                ? pageContents?.paymentText
                : membershipPlan === MembershipPlan.YEARLY
                ? pageContents?.paymentTextYearly
                : pageContents?.paymentTextMonthly
            }
            className="tw-lf-body-regular-sm tw-my-2 tw-hidden desktop:tw-block"
          />
        )}
        {!isCheckoutMonthlyMembershipABTestEnabled &&
          isSubscriptionAvailable &&
          pageContents?.paymentDisclaimer && (
            <p className="tw-lf-body-regular-sm tw-mt-8 desktop:tw-mt-4 desktop:tw-text-left">
              {pageContents.paymentDisclaimer}
            </p>
          )}
        {!isPaymentCTAFromStickyIntoSummaryAbTestEnabled && (
          <GeneralConditions
            isCheckout
            customStyle="tw-mt-8 desktop:tw-hidden"
          />
        )}
        {isCheckoutFlowEnabled && !isMobile && (
          <GeneralConditions
            isCheckout
            customStyle="tw-mt-4 desktop:tw-block tw-hidden"
          />
        )}
      </Body>
      {!isMobile && (
        <NextAction isMembershipSummaryDisplayed={isSubscriptionAvailable}>
          {!isCheckoutFlowEnabled && (
            <>
              <PaymentActions />
              <GeneralConditions
                isCheckout
                customStyle="tw-mt-4 desktop:tw-block tw-hidden tw-text-center"
              />
            </>
          )}
        </NextAction>
      )}
    </>
  );
};

export default CheckoutPayment;
